import { motion } from "framer-motion";

export function Spinner({ size = "md", className = "" }) {
  const sizes = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6",
    xl: "w-10 h-10",
  };

  const strokeWidths = {
    sm: 2.5,
    md: 3,
    lg: 3.5,
    xl: 4,
  };

  return (
    <div className={`inline-flex items-center justify-center ${className}`}>
      <motion.svg
        className={sizes[size]}
        viewBox="0 0 24 24"
        fill="none"
        // 1. Smooth, continuous rotation anchoring the container
        animate={{ rotate: 360 }}
        transition={{
          duration: 1.6, // Slower, more deliberate rotation feels more premium
          repeat: Infinity,
          ease: "linear",
        }}
      >
        <circle
          cx="12"
          cy="12"
          r="9.5"
          stroke="currentColor"
          strokeWidth={strokeWidths[size]}
          className="opacity-15"
        />

        <motion.circle
          cx="12"
          cy="12"
          r="9.5"
          stroke="currentColor"
          strokeWidth={strokeWidths[size]}
          strokeLinecap="round"
          className="opacity-90"
          animate={{
            strokeDasharray: ["1, 150", "90, 150", "90, 150"],
            strokeDashoffset: [0, -35, -124],
          }}
          transition={{
            duration: 1.6,
            repeat: Infinity,
            ease: [0.4, 0, 0.2, 1],
          }}
        />
      </motion.svg>
    </div>
  );
}

export function SpinnerDots({ size = "md", className = "" }) {
  const dotSizes = {
    sm: "w-1 h-1",
    md: "w-1.5 h-1.5",
    lg: "w-2 h-2",
  };

  const gaps = {
    sm: "gap-1",
    md: "gap-1.5",
    lg: "gap-2",
  };

  return (
    <div className={`inline-flex items-center ${gaps[size]} ${className}`}>
      <motion.div
        className={`${dotSizes[size]} bg-current rounded-full`}
        animate={{ opacity: [0.3, 1, 0.3] }}
        transition={{
          duration: 1.2,
          repeat: Infinity,
          delay: 0,
        }}
      />
      <motion.div
        className={`${dotSizes[size]} bg-current rounded-full`}
        animate={{ opacity: [0.3, 1, 0.3] }}
        transition={{
          duration: 1.2,
          repeat: Infinity,
          delay: 0.2,
        }}
      />
      <motion.div
        className={`${dotSizes[size]} bg-current rounded-full`}
        animate={{ opacity: [0.3, 1, 0.3] }}
        transition={{
          duration: 1.2,
          repeat: Infinity,
          delay: 0.4,
        }}
      />
    </div>
  );
}

export function SpinnerPage({ message = "Loading...", compact = false }) {
  return (
    <div className="flex items-center justify-center w-full h-full py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex flex-col items-center gap-4"
      >
        {/* Spinner container */}
        <div className="relative">
          <motion.div
            animate={{
              scale: [1, 1.1, 1],
              opacity: [0.2, 0.1, 0.2],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className={`absolute inset-0 ${compact ? "w-12 h-12" : "w-14 h-14"} -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2 rounded-full bg-gray-900/10`}
          />
          <div
            className={`relative ${compact ? "w-12 h-12" : "w-14 h-14"} rounded-full bg-white shadow-md flex items-center justify-center`}
          >
            <Spinner size={compact ? "md" : "lg"} className="text-gray-900" />
          </div>
        </div>

        {message && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className={`text-gray-700 ${compact ? "text-sm" : "text-base"} font-medium`}
          >
            {message}
          </motion.p>
        )}
      </motion.div>
    </div>
  );
}

export function SpinnerOverlay({ message = "Loading..." }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-gray-900/20 backdrop-blur-sm flex items-center justify-center z-50"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", duration: 0.5 }}
        className="bg-white border border-gray-200 rounded-2xl p-10 shadow-2xl"
      >
        <div className="flex flex-col items-center gap-6">
          {/* Spinner with pulsing background */}
          <div className="relative">
            <motion.div
              animate={{
                scale: [1, 1.15, 1],
                opacity: [0.3, 0.1, 0.3],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="absolute inset-0 w-20 h-20 -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2 rounded-full bg-gray-900/10"
            />
            <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-gray-50 to-gray-100 shadow-inner flex items-center justify-center">
              <Spinner size="xl" className="text-gray-900" />
            </div>
          </div>

          <div className="text-center">
            <p className="text-gray-900 font-semibold text-lg">{message}</p>
            <motion.p
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="text-gray-500 text-sm mt-2"
            >
              This will just take a moment
            </motion.p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
